import React, { useEffect, useRef, useCallback } from 'react';
import { Search, X, GitBranch, Plug, Bot, Code, LayoutDashboard, Box, FileText, LifeBuoy } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSearchStore, SearchResult } from '../store/searchStore';
import { useNavigationStore } from '../store/navigationStore';

const TYPE_CONFIG: Record<string, { i18nKey: string; defaultLabel: string; color: string; icon: React.ReactNode }> = {
  object_type: { i18nKey: 'ontology.objectTypes', defaultLabel: 'Object Type', color: '#7C3AED', icon: <Box size={13} /> },
  pipeline:    { i18nKey: 'pipelines.title',     defaultLabel: 'Pipeline',    color: '#2563EB', icon: <GitBranch size={13} /> },
  connector:   { i18nKey: 'connectors.title',    defaultLabel: 'Data Source',   color: '#0891B2', icon: <Plug size={13} /> },
  agent:       { i18nKey: 'agents.title',        defaultLabel: 'Agent',       color: '#059669', icon: <Bot size={13} /> },
  logic:       { i18nKey: 'logic.title',         defaultLabel: 'Logic',       color: '#D97706', icon: <Code size={13} /> },
  dashboard:   { i18nKey: 'nav.dashboards',      defaultLabel: 'Dashboard',   color: '#DB2777', icon: <LayoutDashboard size={13} /> },
  record:      { i18nKey: 'ontology.records',    defaultLabel: 'Record',      color: '#0891B2', icon: <FileText size={13} /> },
};

export const SearchModal: React.FC = () => {
  const { t } = useTranslation();
  const { isOpen, query, results, loading, open, close, setQuery, search, clear } = useSearchStore();
  const { navigateTo } = useNavigationStore();
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cmd+K belongs to the command palette (useGlobalKeyboard) — binding it
  // here too opened BOTH overlays stacked. Search opens via the top-bar
  // button; this handler only keeps Escape-to-close.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) close();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, close]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  const handleQueryChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const q = e.target.value;
    setQuery(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(q), 200);
  }, [setQuery, search]);

  const handleSelect = useCallback((result: SearchResult) => {
    navigateTo(result.path);
    close();
  }, [navigateTo, close]);

  if (!isOpen) return null;

  // Group results by type
  const grouped: Record<string, SearchResult[]> = {};
  for (const r of results) {
    if (!grouped[r.type]) grouped[r.type] = [];
    grouped[r.type].push(r);
  }
  const typeOrder = ['record', 'object_type', 'pipeline', 'connector', 'agent', 'logic', 'dashboard'];

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={close}
        style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)',
          zIndex: 9998, backdropFilter: 'blur(2px)',
        }}
      />

      {/* Modal */}
      <div style={{
        position: 'fixed', top: '15%', left: '50%', transform: 'translateX(-50%)',
        width: 560, maxWidth: 'calc(100vw - 32px)',
        backgroundColor: 'var(--color-surface)', borderRadius: 10,
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
        zIndex: 9999, overflow: 'hidden',
        border: '1px solid var(--color-border)',
      }}>
        {/* Search input row */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid var(--color-border)', gap: 8 }}>
          <Search size={16} style={{ color: 'var(--color-text-subtle)', flexShrink: 0 }} />
          <input
            ref={inputRef}
            value={query}
            onChange={handleQueryChange}
            placeholder={t('shell.searchPlaceholder', 'Search pipelines, connectors, agents, object types...')}
            style={{
              flex: 1, border: 'none', outline: 'none',
              fontSize: 14, color: 'var(--color-text)', backgroundColor: 'transparent',
            }}
          />
          {query && (
            <button
              onClick={() => { clear(); inputRef.current?.focus(); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--color-text-subtle)', display: 'flex' }}
            >
              <X size={14} />
            </button>
          )}
          <kbd style={{ fontSize: 10, color: 'var(--color-text-subtle)', border: '1px solid var(--color-border)', borderRadius: 3, padding: '1px 5px', fontFamily: 'monospace', backgroundColor: 'var(--color-base)' }}>
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div style={{ maxHeight: 420, overflowY: 'auto' }}>
          {/* Pinned action: report an issue (any user). Shown when idle or when
              the query looks like the user is trying to report something. */}
          {(!query || /rep|iss|bug|tick|prob|fall|error/i.test(query)) && (
            <button
              onClick={() => { close(); window.dispatchEvent(new CustomEvent('nexus:report-issue')); }}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                padding: '9px 14px', background: 'none', border: 'none', cursor: 'pointer',
                borderBottom: '1px solid var(--color-border)', textAlign: 'left',
                transition: 'background-color 60ms',
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--color-interactive-dim)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
            >
              <div style={{
                width: 26, height: 26, borderRadius: 6, flexShrink: 0,
                backgroundColor: '#7C3AED18', display: 'flex', alignItems: 'center',
                justifyContent: 'center', color: '#7C3AED',
              }}>
                <LifeBuoy size={13} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-text)' }}>{t('shell.reportIssueAction', 'Report an issue')}</div>
                <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{t('shell.reportIssueActionDesc', 'Something broken? Send it to the platform team with screenshots')}</div>
              </div>
              <div style={{ fontSize: 10, color: '#7C3AED', backgroundColor: '#7C3AED15', borderRadius: 3, padding: '1px 5px', flexShrink: 0 }}>
                {t('shell.actionBadge', 'Action')}
              </div>
            </button>
          )}

          {!query && (
            <div style={{ padding: '24px 16px', textAlign: 'center', color: 'var(--color-text-subtle)', fontSize: 13 }}>
              {t('shell.startTypingSearch', 'Start typing to search across all your data')}
            </div>
          )}

          {query && loading && (
            <div style={{ padding: '20px 16px', textAlign: 'center', color: 'var(--color-text-subtle)', fontSize: 13 }}>
              {t('shell.searching', 'Searching...')}
            </div>
          )}

          {query && !loading && results.length === 0 && (
            <div style={{ padding: '20px 16px', textAlign: 'center', color: 'var(--color-text-subtle)', fontSize: 13 }}>
              {t('shell.noResultsFor', { query, defaultValue: `No results for "${query}"` })}
            </div>
          )}

          {!loading && typeOrder.filter(tKey => grouped[tKey]?.length).map(type => {
            const config = TYPE_CONFIG[type];
            const label = config ? t(config.i18nKey, config.defaultLabel) : type;
            const color = config?.color || '#64748B';
            const icon = config?.icon || <Box size={13} />;
            return (
              <div key={type}>
                {/* Section header */}
                <div style={{
                  padding: '6px 14px 3px',
                  fontSize: 10, fontWeight: 600, color: 'var(--color-text-subtle)',
                  textTransform: 'uppercase', letterSpacing: '0.06em',
                  backgroundColor: 'var(--color-base)',
                  borderBottom: '1px solid var(--color-border)',
                }}>
                  {label}
                </div>
                {grouped[type].map(result => (
                  <button
                    key={result.id}
                    onClick={() => handleSelect(result)}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center',
                      gap: 10, padding: '9px 14px',
                      background: 'none', border: 'none', cursor: 'pointer',
                      borderBottom: '1px solid var(--color-border)',
                      textAlign: 'left', transition: 'background-color 60ms',
                    }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--color-interactive-dim)'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
                  >
                    {/* Type icon */}
                    <div style={{
                      width: 26, height: 26, borderRadius: 6, flexShrink: 0,
                      backgroundColor: `${color}18`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: color,
                    }}>
                      {icon}
                    </div>
                    {/* Text */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {result.title}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--color-text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {result.subtitle}
                      </div>
                    </div>
                    {/* Score badge */}
                    <div style={{
                      fontSize: 10, color: color,
                      backgroundColor: `${color}15`,
                      borderRadius: 3, padding: '1px 5px', flexShrink: 0,
                    }}>
                      {label}
                    </div>
                  </button>
                ))}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{
          padding: '6px 14px', borderTop: '1px solid var(--color-border)',
          display: 'flex', alignItems: 'center', gap: 12,
          backgroundColor: 'var(--color-base)',
        }}>
          <span style={{ fontSize: 10, color: 'var(--color-text-subtle)' }}>
            <kbd style={{ fontFamily: 'monospace', border: '1px solid var(--color-border)', borderRadius: 3, padding: '0 4px', fontSize: 10, backgroundColor: 'var(--color-surface)' }}>↵</kbd> {t('common.toNavigate', 'to navigate')}
          </span>
          <span style={{ fontSize: 10, color: 'var(--color-text-subtle)' }}>
            <kbd style={{ fontFamily: 'monospace', border: '1px solid var(--color-border)', borderRadius: 3, padding: '0 4px', fontSize: 10, backgroundColor: 'var(--color-surface)' }}>esc</kbd> {t('common.toClose', 'to close')}
          </span>
        </div>
      </div>
    </>
  );
};

export default SearchModal;
